<template>
    <main class="settings">
        <div class="settings-cards">
            <div v-if="isMasterUser && accountDomain" class="card">
                <account-domain-form
                    :current-domain="accountDomain"
                    @updated="getAccountDomain"
                ></account-domain-form>
            </div>

            <div class="card">
                <account-details-form
                    ref="bodyAccountDetails"
                    v-model="accountDetails"
                    @input="inputAccountDetails"
                    @valid="saveAccountDetails"
                ></account-details-form>
                <div class="card-footer">
                    <button
                        class="btn btn-primary btn-extra-round"
                        @click="checkUserDataConfirmation"
                    >
                        {{ $t("message.generics.message.save") }}
                    </button>
                </div>
            </div>
            <div class="card">
                <password-form
                    ref="bodyPasswordDetails"
                    v-model="passwordDetails"
                    @input="inputPasswordDetails"
                    @valid="changePassword"
                ></password-form>
                <div class="card-footer">
                    <button
                        class="btn btn-primary btn-extra-round"
                        @click="$refs.bodyPasswordDetails.validate()"
                    >
                        {{ $t("message.generics.message.save") }}
                    </button>
                </div>
            </div>
        </div>

        <app-confirmation-modal
            :value="!!confirmingEmailAlteration"
            :confirm-label="$t('message.generics.message.confirm')"
            @confirm="confirmEmailAlteration"
            @cancel="confirmingEmailAlteration = false"
        >
            <span slot="heading">
                {{ $t("message.settings.message.emailChange") }}
            </span>
            <span>
                {{ $t("message.settings.message.emailChangeHint") }}
            </span>
        </app-confirmation-modal>

        <locale-priority
            v-if="isMasterUser && isPriorityLoaded && !isPartner"
            :initial-list="priorityList"
            title
            drag
            @end="alterPriority"
        />
    </main>
</template>

<script>
import AppConfirmationModal from "@components/ConfirmationModal";
import LocalePriority from "@views/locals/components/LocalePriority";
import EventBus from "@packages/event-bus";
import PasswordForm from "./components/PasswordForm";
import AccountDetailsForm from "./components/AccountDetailsForm";
import AccountDomainForm from "./components/AccountDomainForm";
import { redirectUtils } from "../../packages/util/redirect/redirectUtils";

export default {
    components: {
        PasswordForm,
        AccountDetailsForm,
        LocalePriority,
        AppConfirmationModal,
        AccountDomainForm,
    },

    data() {
        return {
            confirmingEmailAlteration: false,
            accountDomain: "",
            accountDetails: {
                id: "",
                nome: "",
                login: "",
                paginaPosAutenticacao: "",
            },
            passwordDetails: {
                atual: "",
                nova: "",
                confirmacao: "",
            },
            priorityList: [],
            isPriorityLoaded: false,
        };
    },

    beforeMount() {
        const url = this.isPartner ? redirectUtils.mountUrl('/new/parceiro/configuracoes/geral') : redirectUtils.mountUrl('/new/estabelecimento/configuracoes/geral');
        location.assign(url)
    },

    computed: {
        user() {
            return this.$store.state.users.user;
        },
        isMasterUser() {
            return this.user.permissions.includes("MASTER");
        },
        isPartner() {
            return this.$store.state.users.user.tipo === "PARCEIRO";
        },
    },

    methods: {
        async checkUserDataConfirmation() {
            if (this.accountDetails.login !== this.user.login) {
                this.confirmingEmailAlteration = true;
            } else {
                this.$refs.bodyAccountDetails.validate();
            }
        },
        confirmEmailAlteration() {
            this.$refs.bodyAccountDetails.validate();
        },
        async saveAccountDetails() {
            try {
                EventBus.$emit("SHOW_SPINNER");

                await this.$api.userLogin.changeAccountDetails(
                    this.accountDetails
                );

                EventBus.$emit("show-message", {
                    type: "SUCCESS",
                    message: this.$t(
                        "message.successMessages.message.userUpdated"
                    ),
                });

                this.$store.commit("SET_USER", {
                    ...this.user,
                    ...this.accountDetails,
                });

                if (this.confirmingEmailAlteration) {
                    this.$router.push("/logout");
                }
            } catch (error) {
                console.log(error);
            } finally {
                this.confirmingEmailAlteration = false;
                EventBus.$emit("CANCEL_SPINNER");
            }
        },
        async changePassword() {
            var data = {
                id: this.accountDetails.id,
                oldPassword: this.passwordDetails.atual,
                newPassword: this.passwordDetails.nova,
            };
            const resultado = await this.$api.userLogin.alterPassword(data);
            EventBus.$emit("show-message", {
                type: resultado.status,
                message: resultado.message,
            });
            this.passwordDetails = {
                atual: "",
                nova: "",
                confirmacao: "",
            };
        },

        inputPasswordDetails(value) {
            this.passwordDetails = value;
        },

        inputAccountDetails(value) {
            this.accountDetails = value;
        },

        async alterPriority(list) {
            try {
                const res = await this.$api.localPriority.alterPriority({
                    priorityArray: list.map((localPriority) => {
                        return localPriority.level;
                    }),
                });

                EventBus.$emit("show-message", {
                    type: "SUCCESS",
                    message: res.message,
                    timeout: 2000,
                });
            } catch (error) {
                console.log(error);
            }
        },

        async getAccountDomain() {
            try {
                this.accountDomain = await this.$api.account.getAccountDomain();
            } catch (error) {
                console.error(error);
            }
        },
    },

    async created() {
        this.accountDetails = {
            id: this.user.id,
            nome: this.user.nome,
            login: this.user.login,
            paginaPosAutenticacao: this.user.paginaPosAutenticacao,
        };

        if(this.isMasterUser) {
            this.getAccountDomain();
        }

        if (!this.isPartner) {
            this.priorityList = await this.$api.localPriority.search(true);

            this.isPriorityLoaded = true;
        }
    },
};
</script>

<style lang="scss" scoped>
@import "~@variables";

.settings {
    .settings-cards {
        display: flex;
        gap: 40px;
        margin-bottom: 50px;
        height: 100%;
        max-height: 366px;
        justify-content: center;

        .cards {
            height: calc(30% - 60px);
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            grid-column-gap: 40px;
        }

        .main {
            margin: 34px 0 20px 0;
            height: calc(70% - 20px);
            display: grid;
            grid-template-columns: minmax(510px, auto) minmax(390px, 520px);
            grid-template-rows: auto;
            grid-template-areas: "impressions comments";
            grid-column-gap: 40px;
        }

        .card {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 30px;
            border: 1px solid $grey-3;
            border-radius: 20px;
            width: 100%;
            max-width: 350px;
        }

        button {
            width: 120px;
        }

        .card-footer {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: auto;
        }
    }
}
</style>
